export interface ScriptLine {
    character: string | null;
    dialogue: string;
    isStageDirection: boolean;
    recordedAudioId?: string | null;
}

export interface RecordedAudioEntry {
    character: string;
    text: string;
    audioId: string;
}

export interface RecordedLine {
    character: string;
    text: string;
    audioId?: string;
}

export interface ClassifyResult {
    type: 'STAGE' | 'DIALOGUE';
    characterGuess: string | null;
}

const NON_CHARACTER_WORDS = new Set([
    'THE', 'AND', 'BUT', 'OR', 'WITH', 'FROM', 'INTO', 'ONTO', 'UPON',
    'ALL', 'END', 'ACT', 'NOTE', 'BEAT', 'INT', 'EXT', 'CONTINUED',
    'FADE', 'CUT', 'DISSOLVE', 'BLACKOUT', 'LIGHTS', 'SOUND', 'MUSIC',
    'VIDEO', 'PROJECTION', 'PLAY', 'STOP', 'CUE', 'SFX', 'SCENE',
    'ISBN', 'EPUB', 'CONTENTS', 'ACKNOWLEDGEMENTS', 'ACKNOWLEDGMENTS',
    'COPYRIGHT', 'WWW', 'HTTP', 'HTTPS', 'BOOKS', 'PUBLISHING',
]);

function normalizeCue(text: string): string {
    return String(text || '')
        .trim()
        .replace(/\s+/g, ' ')
        .replace(/:\s*$/, '');
}

export function isLikelyCharacterDisplayName(name: string): boolean {
    const normalized = normalizeCue(name);
    if (!normalized) return false;
    if (normalized.length > 40) return false;
    if (!/^[A-Za-z0-9\s().'\/&-]+$/.test(normalized)) return false;
    if (/[a-z]/.test(normalized) && !/^[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*$/.test(normalized)) return false;

    const tokens = normalized.split(/\s*\/\s*|\s+/).filter(Boolean);
    if (tokens.length === 0 || tokens.length > 4) return false;
    if (tokens.some(token => NON_CHARACTER_WORDS.has(token.toUpperCase()))) return false;

    const letters = normalized.match(/[A-Za-z]/g) || [];
    if (letters.length === 0) return false;
    const uppercaseLetters = normalized.match(/[A-Z]/g) || [];
    return uppercaseLetters.length / letters.length >= 0.8 || /^[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*$/.test(normalized);
}

export function classifyLine(text: string, characterSet: string[] = [], sawFirstAllCapsHeading: boolean = false): ClassifyResult & { sawFirstAllCapsHeading: boolean } {
    const trimmed = String(text || '').trim();
    if (!trimmed) {
        return { type: 'STAGE', characterGuess: null, sawFirstAllCapsHeading };
    }

    const upper = trimmed.toUpperCase();
    const isAllCaps = trimmed === upper && !/[a-z]/.test(trimmed);
    const hasActionCue = /^(?:\(|\[|for a moment|suddenly|meanwhile|after a moment|pause|beat|silence\b)/i.test(trimmed);

    if (hasActionCue) {
        return { type: 'STAGE', characterGuess: null, sawFirstAllCapsHeading };
    }

    if (isLikelyCharacterDisplayName(trimmed)) {
        return { type: 'DIALOGUE', characterGuess: normalizeCue(trimmed), sawFirstAllCapsHeading };
    }

    if (isAllCaps && trimmed.split(/\s+/).length >= 4) {
        return { type: 'STAGE', characterGuess: null, sawFirstAllCapsHeading: true };
    }

    const knownMatch = characterSet.find(char => trimmed.startsWith(char + ' ') || trimmed.startsWith(char + ':'));
    if (knownMatch) {
        return { type: 'DIALOGUE', characterGuess: knownMatch, sawFirstAllCapsHeading };
    }

    return { type: 'DIALOGUE', characterGuess: null, sawFirstAllCapsHeading };
}
